import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah, formatDate } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const TONE = {
  Sehat: "bg-[#0F5257]/10 text-[#0F5257]",
  Tipis: "bg-amber-100 text-amber-900",
  Minus: "bg-red-100 text-red-800",
  "Support Event": "bg-[#F2EEE7] text-[#536263]",
};
const pct = (m) => (m == null ? "-" : `${m.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`);
const monthLabel = (ym) => (ym ? `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : "Tanpa tanggal");

function Stat({ label, value, tone, testid }) {
  return (
    <div className="card-editorial p-4" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#B87333]">{label}</div>
      <div className={`font-serif-display text-xl sm:text-2xl font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function ProjectRow({ r }) {
  return (
    <Link to={`/projects/${r.project_id}`} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center p-3 rounded-lg hover:bg-[#FAF7F2] border-b border-[#E2DDD3] last:border-0" data-testid={`profit-row-${r.project_id}`}>
      <div className="col-span-2 md:col-span-4 min-w-0">
        <div className="font-medium text-sm truncate">{r.name}</div>
        <div className="text-xs text-[#8A999A]">{formatDate(r.event_date)} · {r.segment} · {r.cost_count} transaksi</div>
      </div>
      <div className="md:col-span-2 text-right font-mono-num text-sm"><span className="md:hidden text-[10px] text-[#8A999A] mr-1">Kontrak</span>{r.contract_value ? formatRupiah(r.contract_value) : "-"}</div>
      <div className="md:col-span-2 text-right font-mono-num text-sm text-red-700"><span className="md:hidden text-[10px] text-[#8A999A] mr-1">Biaya</span>{formatRupiah(r.cost)}</div>
      <div className={`md:col-span-2 text-right font-mono-num text-sm font-semibold ${r.profit < 0 ? "text-red-700" : "text-[#0F5257]"}`}><span className="md:hidden text-[10px] text-[#8A999A] mr-1 font-normal">Laba</span>{r.profit == null ? "-" : formatRupiah(r.profit)}</div>
      <div className="md:col-span-2 flex items-center justify-end gap-2">
        <span className="font-mono-num text-sm">{pct(r.margin)}</span>
        <span className={`chip ${TONE[r.status]}`} data-testid={`profit-status-${r.project_id}`}>{r.status}</span>
      </div>
    </Link>
  );
}

export default function Profit() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("Semua");
  const [segment, setSegment] = useState("Semua");

  useEffect(() => { api.get("/reports/profit", { params: year ? { year } : {} }).then(r => setData(r.data)); }, [year]);

  const rows = useMemo(() => (data?.rows || []).filter(r =>
    (status === "Semua" || r.status === status) && (segment === "Semua" || r.segment === segment)), [data, status, segment]);
  const byMonth = useMemo(() => rows.reduce((acc, r) => { (acc[r.month] = acc[r.month] || []).push(r); return acc; }, {}), [rows]);

  if (!data) return <div className="p-6">Memuat...</div>;
  const t = data.totals;
  const segments = ["Semua", ...new Set(data.rows.map(r => r.segment))];
  return (
    <div className="p-4 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Laba per Project</h1>
          <p className="text-sm text-[#536263]">Kontrak dikurangi biaya produksi (pengeluaran disetujui). Sehat ≥ {data.healthy_margin}%, Tipis &lt; {data.healthy_margin}%, Minus &lt; 0</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        <select value={year} onChange={e => setYear(e.target.value)} className="border border-[#E2DDD3] rounded-lg px-3 h-11" data-testid="profit-year-select">
          {data.years.map(y => <option key={y} value={y}>{y}</option>)}
          <option value="">Semua tahun</option>
        </select>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Kontrak" value={formatRupiah(t.contract)} testid="profit-total-contract" />
        <Stat label="Biaya Produksi" value={formatRupiah(t.cost)} tone="text-red-700" testid="profit-total-cost" />
        <Stat label="Laba Kotor" value={formatRupiah(t.profit)} tone={t.profit < 0 ? "text-red-700" : "text-[#0F5257]"} testid="profit-total-profit" />
        <Stat label="Margin Rata-rata" value={pct(t.margin)} testid="profit-total-margin" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {["Semua", "Sehat", "Tipis", "Minus", "Support Event"].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`profit-filter-${s.toLowerCase().replace(" ", "-")}`}
            className={`chip ${status === s ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>
            {s}{s !== "Semua" && ` (${t.counts[s] || 0})`}
          </button>
        ))}
        <select value={segment} onChange={e => setSegment(e.target.value)} className="ml-auto border border-[#E2DDD3] rounded-lg px-3 h-9 text-sm" data-testid="profit-segment-select">
          {segments.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="card-editorial p-2 sm:p-4" data-testid="profit-table">
        <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-2 text-[11px] uppercase tracking-wider text-[#B87333]">
          <div className="col-span-4">Project</div><div className="col-span-2 text-right">Nilai Kontrak</div>
          <div className="col-span-2 text-right">Biaya Produksi</div><div className="col-span-2 text-right">Laba Kotor</div>
          <div className="col-span-2 text-right">% Laba · Status</div>
        </div>
        {Object.entries(byMonth).map(([m, list]) => {
          const c = list.reduce((a, r) => a + (r.contract_value || 0), 0);
          const k = list.filter(r => r.contract_value).reduce((a, r) => a + r.cost, 0);
          return (
            <div key={m || "none"} className="mb-3">
              <div className="flex items-center justify-between px-3 py-2 bg-[#FAF7F2] rounded-lg">
                <span className="font-serif-display font-semibold">{monthLabel(m)}</span>
                <span className="text-xs font-mono-num text-[#536263]">Laba {formatRupiah(c - k)} · {pct(c ? ((c - k) / c) * 100 : null)}</span>
              </div>
              {list.map(r => <ProjectRow key={r.project_id} r={r} />)}
            </div>
          );
        })}
        {rows.length === 0 && <p className="p-6 text-center text-sm text-[#8A999A]">Tidak ada project</p>}
      </div>

      {data.support.length > 0 && (status === "Semua" || status === "Support Event") && (
        <div className="card-editorial p-5 space-y-3" data-testid="profit-support">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-serif-display text-xl font-semibold flex items-center gap-2"><TrendingUp size={18} />Support Event / Belum Dicocokkan</h2>
            <Link to="/sheets" className="text-xs text-[#B87333]" data-testid="profit-go-matcher">Cocokkan ke project →</Link>
          </div>
          <p className="text-sm text-[#536263]">Biaya dari Petty Cash yang namanya belum cocok dengan project. Tidak dihitung ke laba di atas.</p>
          {data.support.map(s => (
            <div key={s.label} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-lg gap-3">
              <div className="min-w-0"><div className="text-sm font-medium truncate">{s.label}</div><div className="text-xs text-[#8A999A]">{s.cost_count} transaksi</div></div>
              <span className="font-mono-num text-sm text-red-700 shrink-0">{formatRupiah(s.cost)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
